import React, { useCallback, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useListLocationThreadsQuery, useLocalGetStudentsQuery } from '../../store/api/localApi';
import { Thread, ThreadStatus } from '../../types';
import { studentDisplayName } from '../../utils/student';
import ThreadView from './ThreadView';

// ── LocationThreadInbox ──────────────────────────────────────────────────────
//
// The shared STAFF inbox: every parent thread at one location, newest activity
// first. Selecting a row swaps the body for the SHARED `ThreadView` — the same
// renderer the parent uses, unchanged. The reply box comes with it, and the
// backend decides the sender side and the OPEN -> ANSWERED flip, so nothing
// here branches on the viewer's role.
//
// Thread detail is in-page state rather than a route because `messages/:messageId`
// is already claimed by the announcement MessageDetailPage at all four scope
// prefixes; a sibling `messages/:threadId` route would be ambiguous with it.

/** The filter is a view concern, so 'ALL' (the API's explicit no-filter option) is a real choice. */
type StatusFilter = ThreadStatus | 'ALL';

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: ThreadStatus.OPEN, label: 'Open' },
  { value: ThreadStatus.ANSWERED, label: 'Answered' },
];

function formatLastActivity(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  return then.toLocaleString('en-IE', {
    timeZone: 'Europe/Dublin',
    hour12: false,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const StatusChip: React.FC<{ threadId: string; status: ThreadStatus }> = ({ threadId, status }) => {
  const isOpen = status === ThreadStatus.OPEN;
  return (
    <span
      data-testid={`thread-status-${threadId}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium flex-shrink-0 ${
        isOpen ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
      }`}
    >
      {isOpen ? <ClockIcon className="h-3 w-3" /> : <CheckCircleIcon className="h-3 w-3" />}
      {isOpen ? 'Waiting for a reply' : 'Answered'}
    </span>
  );
};

const ThreadRow: React.FC<{
  thread: Thread;
  studentName: string;
  onOpen: (threadId: string) => void;
}> = ({ thread, studentName, onOpen }) => (
  <li>
    <button
      type="button"
      onClick={() => onOpen(thread.id)}
      data-testid={`thread-inbox-row-${thread.id}`}
      className="w-full flex items-start gap-3 px-4 py-4 sm:px-6 text-left hover:bg-gray-50"
    >
      <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-semibold text-gray-900 truncate"
          data-testid={`thread-inbox-student-${thread.id}`}
        >
          {studentName}
        </p>
        <p className="mt-0.5 text-sm text-gray-700 truncate">{thread.lastMessagePreview}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          {thread.messageCount} message{thread.messageCount === 1 ? '' : 's'} ·{' '}
          {formatLastActivity(thread.lastMessageAt)}
        </p>
      </div>
      <StatusChip threadId={thread.id} status={thread.status} />
    </button>
  </li>
);

/**
 * One page of the inbox, keyed by its cursor. Each page owns its own query hook,
 * so "Load more" is simply mounting the next page — there is no hand-rolled
 * accumulator that could drift from the RTK cache.
 *
 * PAGINATION CONTRACT: the backend applies the status filter AFTER the DynamoDB
 * page limit, so a filtered page can come back short or even EMPTY while
 * `lastKey` is still set. The page therefore reports `lastKey` up verbatim, and
 * the parent drives "load more" off that alone — a page's length is never
 * evidence that the walk has ended.
 */
const ThreadPage: React.FC<{
  locationId: string;
  status: StatusFilter;
  cursor?: string;
  isFirstPage: boolean;
  studentNameById: Record<string, string>;
  onOpenThread: (threadId: string) => void;
  onSettled: (result: { threadCount: number; lastKey?: string }) => void;
}> = ({ locationId, status, cursor, isFirstPage, studentNameById, onOpenThread, onSettled }) => {
  const { data, isLoading, error } = useListLocationThreadsQuery(
    { locationId, status, cursor },
    { skip: !locationId }
  );

  const threads: Thread[] = useMemo(() => data?.threads ?? [], [data]);
  const lastKey = data?.lastKey;

  React.useEffect(() => {
    if (isLoading || error) return;
    onSettled({ threadCount: threads.length, lastKey });
  }, [isLoading, error, threads.length, lastKey, onSettled]);

  if (isLoading) {
    return (
      <p
        className="px-4 py-8 sm:px-6 text-sm text-gray-500 text-center"
        data-testid={isFirstPage ? 'thread-inbox-loading' : 'thread-inbox-page-loading'}
      >
        Loading conversations…
      </p>
    );
  }

  if (error) {
    return (
      <div
        className="px-4 py-8 sm:px-6 text-center"
        data-testid={isFirstPage ? 'thread-inbox-error' : 'thread-inbox-page-error'}
      >
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
        <p className="mt-2 text-sm text-gray-500">
          Couldn&apos;t load these conversations. Please try again later.
        </p>
      </div>
    );
  }

  if (threads.length === 0) return null;

  // The API sorts newest lastMessageAt first (GSI2, ScanIndexForward:false).
  // Rendering in received order preserves that — do NOT re-sort here.
  return (
    <ul className="divide-y divide-gray-100">
      {threads.map((thread) => (
        <ThreadRow
          key={thread.id}
          thread={thread}
          studentName={studentNameById[thread.studentId] ?? studentDisplayName(null)}
          onOpen={onOpenThread}
        />
      ))}
    </ul>
  );
};

/** What one mounted page has reported back, indexed by page position. */
interface PageState {
  threadCount: number;
  lastKey?: string;
}

const LocationThreadInbox: React.FC = () => {
  const { effectiveLocationId } = useLocationContext();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);

  // Cursors for the pages loaded beyond the first. Cleared on a filter change,
  // since a cursor is only meaningful inside one filtered walk.
  const [cursors, setCursors] = useState<string[]>([]);
  const [pageStates, setPageStates] = useState<Record<number, PageState>>({});

  // Threads carry only studentId, so names come from the location roster — the
  // same query the other staff screens already populate in the RTK cache.
  const { data: students } = useLocalGetStudentsQuery(effectiveLocationId ?? '', {
    skip: !effectiveLocationId,
  });
  const studentNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const student of students ?? []) map[student.id] = studentDisplayName(student);
    return map;
  }, [students]);

  const changeFilter = (next: StatusFilter) => {
    setStatus(next);
    setCursors([]);
    setPageStates({});
    setOpenThreadId(null);
  };

  const handlePageSettled = useCallback((pageIndex: number, result: PageState) => {
    setPageStates((prev) => {
      const existing = prev[pageIndex];
      if (
        existing &&
        existing.threadCount === result.threadCount &&
        existing.lastKey === result.lastKey
      ) {
        return prev;
      }
      return { ...prev, [pageIndex]: result };
    });
  }, []);

  // Org-level scopes (`/messages`, `/org/:orgId/messages`) may have no location
  // in scope. The inbox is location-shaped, so ask for one rather than firing a
  // query with an undefined id.
  if (!effectiveLocationId) {
    return (
      <div
        className="bg-white rounded-lg shadow px-4 py-10 text-center"
        data-testid="thread-inbox-no-location"
      >
        <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-2 text-sm text-gray-500">
          Select a location to see its parent conversations.
        </p>
      </div>
    );
  }

  if (openThreadId) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setOpenThreadId(null)}
          data-testid="thread-inbox-back"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          All conversations
        </button>
        {/* The SHARED renderer, mounted verbatim. It owns `thread-status-<id>`
            and the reply box while open, which is why the list is not rendered
            underneath — only one chip per thread is ever on screen. */}
        <ThreadView threadId={openThreadId} title="Parent conversation" />
      </div>
    );
  }

  const pageCursors: (string | undefined)[] = [undefined, ...cursors];

  // Only the LAST mounted page's lastKey says whether more remain. A short or
  // empty page proves nothing, because the status filter runs after the page limit.
  const lastPageState = pageStates[pageCursors.length - 1];
  const nextCursor = lastPageState?.lastKey;
  const allPagesSettled = pageCursors.every((_, index) => pageStates[index] !== undefined);
  const totalThreads = pageCursors.reduce(
    (sum, _, index) => sum + (pageStates[index]?.threadCount ?? 0),
    0
  );
  // "Nothing here" is only honest once every page settled AND no cursor remains.
  const isTrulyEmpty = allPagesSettled && totalThreads === 0 && !nextCursor;

  return (
    <section
      className="bg-white rounded-lg shadow overflow-hidden"
      data-testid="location-thread-inbox"
    >
      <div className="px-4 py-3 sm:px-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">Parent messages</h2>
          <p className="text-xs text-gray-500">
            Conversations started by parents at this location. Replying marks a thread answered.
          </p>
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Filter by status">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              data-testid={`thread-filter-${filter.value}`}
              onClick={() => changeFilter(filter.value)}
              aria-pressed={status === filter.value}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                status === filter.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {pageCursors.map((cursor, index) => (
        <ThreadPage
          key={cursor ?? '__first__'}
          locationId={effectiveLocationId}
          status={status}
          cursor={cursor}
          isFirstPage={index === 0}
          studentNameById={studentNameById}
          onOpenThread={setOpenThreadId}
          onSettled={(result) => handlePageSettled(index, result)}
        />
      ))}

      {isTrulyEmpty && (
        <p
          className="px-4 py-8 sm:px-6 text-sm text-gray-500 text-center"
          data-testid="thread-inbox-empty"
        >
          {status === 'ALL'
            ? 'No parent conversations at this location yet.'
            : `No ${status === ThreadStatus.OPEN ? 'open' : 'answered'} conversations.`}
        </p>
      )}

      {nextCursor && (
        <div className="px-4 py-3 sm:px-6 border-t border-gray-100 text-center">
          <button
            type="button"
            data-testid="thread-inbox-load-more"
            onClick={() => setCursors((prev) => [...prev, nextCursor])}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Load more
          </button>
        </div>
      )}
    </section>
  );
};

export default LocationThreadInbox;
